import type { ReactElement } from 'react';
import type { PrintTemplateDocument } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { cn } from '#client/lib/cn';
import { BodySection } from './sections/BodySection';
import { FooterSection } from './sections/FooterSection';
import { HeaderSection } from './sections/HeaderSection';
import { PageSection } from './sections/PageSection';
import { SignaturesSection } from './sections/SignaturesSection';
import { ThemeSection } from './sections/ThemeSection';
import { WatermarkSection } from './sections/WatermarkSection';
import {
  TEMPLATE_SECTIONS,
  type TemplateSectionId,
  sectionSubtitle,
} from './template-sections.logic';

interface TemplateSectionsProps {
  document: PrintTemplateDocument;
  onPatch: <K extends keyof PrintTemplateDocument>(
    key: K,
    changes: Partial<PrintTemplateDocument[K]>,
  ) => void;
  section: string;
  onSectionChange: (section: string) => void;
}

/**
 * The builder's seven sections, one open at a time.
 *
 * An accordion rather than the app's `Tabs`: these are parts of one document
 * being edited, not alternative views of it, so the open one has to stay open
 * while the preview beside it re-renders — and every closed section's state is
 * still very much part of what is being saved. `Tabs` also carries counts and a
 * `tablist` role that would be a lie here.
 *
 * Sections stay mounted-on-open rather than all at once, because each holds a
 * dozen or more inputs and the panel is a scrolling column; showing all six
 * would bury the one being worked on.
 *
 * Signatures sits between the results table and the footer because that is
 * where it prints: at the foot of every page, just above the footer.
 */
export function TemplateSections({
  document,
  onPatch,
  section,
  onSectionChange,
}: TemplateSectionsProps) {
  return (
    <div className="c-builder__sections">
      {TEMPLATE_SECTIONS.map((item) => {
        const open = item.id === section;

        return (
          <Card key={item.id} flush className="c-builder__section">
            <button
              type="button"
              className={cn('c-builder__section-head', open && 'is-open')}
              aria-expanded={open}
              aria-controls={`section-${item.id}`}
              onClick={() => onSectionChange(open ? '' : item.id)}
            >
              <span className="c-builder__section-title">{item.title}</span>
              <span className="c-builder__section-subtitle">
                {sectionSubtitle(item.id, document)}
              </span>
            </button>

            {open && (
              <div className="c-builder__section-body" id={`section-${item.id}`}>
                <SectionContent id={item.id} document={document} onPatch={onPatch} />
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/** The open section's controls. Typed so a section with no case here fails to compile. */
function SectionContent({
  id,
  document,
  onPatch,
}: {
  id: TemplateSectionId;
  document: PrintTemplateDocument;
  onPatch: TemplateSectionsProps['onPatch'];
}): ReactElement {
  switch (id) {
    case 'page':
      return <PageSection page={document.page} onChange={(patch) => onPatch('page', patch)} />;
    case 'theme':
      return <ThemeSection theme={document.theme} onChange={(patch) => onPatch('theme', patch)} />;
    case 'header':
      return <HeaderSection document={document} onChange={(patch) => onPatch('header', patch)} />;
    case 'body':
      return <BodySection body={document.body} onChange={(patch) => onPatch('body', patch)} />;
    case 'signatures':
      return (
        <SignaturesSection
          signatures={document.signatures}
          onChange={(patch) => onPatch('signatures', patch)}
        />
      );
    case 'footer':
      return <FooterSection document={document} onChange={(patch) => onPatch('footer', patch)} />;
    case 'watermark':
      return (
        <WatermarkSection
          watermark={document.watermark}
          onChange={(patch) => onPatch('watermark', patch)}
        />
      );
  }
}
